// The PLATFORM axis only: admin = system-wide. Vendor powers come from org
// membership; the former 'developer' platform role is retired.
export enum UserRole {
    Admin = "admin",
    Customer = "customer"
}

// The vendor axis of authorization: org membership roles (users.role stays
// the platform axis — admin means system-wide).
export enum OrgRole {
    Admin = "admin",
    Developer = "developer"
}

// Displayed without the "vendor" qualifier — org roles read plain
// (system admins know the platform/org distinction).
export const orgRoleLabels: Record<OrgRole, string> = {
    [OrgRole.Admin]: "Admin",
    [OrgRole.Developer]: "Developer"
}

export interface OrganizationMembership {
    readonly orgId: string
    readonly orgName: string
    readonly orgRole: OrgRole
}

export interface Organization {
    readonly id: string        // the handle — permanent upload namespace/URL component
    readonly name: string      // display name
    readonly websiteUrl: string | null
    // the org logo (rev-keyed URL + scaler-generated width variants, the
    // sku-image conventions)
    readonly imageUrl: string | null
    readonly availableWidths: number[] | null
    // iLok switched on for the org's skus (set by hand in the DB): gates the
    // per-issuance "allow iLok" toggle in the grant / codes dialogs
    readonly ilokEnabled: boolean
    readonly createdAt: string
}

// A user is privileged (sees the admin surface) on either axis: platform
// role, or membership in at least one organization.
export function isPrivilegedUser(me: { role: UserRole, memberships?: OrganizationMembership[] }): boolean {
    return me.role === UserRole.Admin || (me.memberships?.length ?? 0) > 0
}

// The legal documents an account consents to, each versioned on its own.
export type PolicyKind = "terms" | "privacy"

export const POLICY_NAMES: Record<PolicyKind, string> = {
    terms: "Terms of Use",
    privacy: "Privacy Policy",
}

// One document's consent state (GET /me `policies`): the app is gated
// while any document has acceptanceRequired, until
// POST /me/policies/acceptances records consent to each (one checkbox per
// document).
export interface Policy {
    kind: PolicyKind
    currentVersion: string
    url: string
    acceptedVersion?: string
    acceptanceRequired: boolean
}

export interface User {
    id: string
    role: UserRole
    firstname?: string
    lastname?: string
    email?: string
    // undefined = unknown (Cognito didn't report the attribute)
    emailVerified?: boolean
    imageUrl?: string
    // false = account disabled in Cognito; undefined = unknown
    enabled?: boolean
    // Optional: absent when talking to a backend that predates MFA support;
    // treat undefined as false.
    mfaEnabled?: boolean
    // the vendor axis: absent = none (or a backend predating organizations)
    memberships?: OrganizationMembership[]
    // the version of each legal document the account accepted (Cognito
    // custom:<kind>_version); a document is absent until accepted
    policyVersions?: Partial<Record<PolicyKind, string>>
    createdAt: string
    updatedAt: string
}

// Per-category email opt-outs (GET/PATCH /me/notification-preferences);
// account-critical emails (refunds, account status) have no switch
// Only product news is optional; transactional mail (purchases, billing,
// security) is always sent. Backed by the custom:product_news Cognito attribute.
export interface NotificationPreferences {
    productUpdates: boolean
}

// Public profile of any user (GET /me/users/{userId}) — name and avatar only;
// email, role and account status stay on the admin-only User.
export interface UserProfile {
    readonly id: string
    readonly firstname?: string
    readonly lastname?: string
    readonly imageUrl?: string
}

export enum PaymentType {
    OneOff = "oneoff",
    Subscription = "subscription",
}

export interface Product {
    readonly id: string
    readonly name: string
    readonly ownerId: string
    readonly url?: string
    readonly imageUrl?: string
    readonly availableWidths?: number[]
    readonly skuIds: string[]
    readonly valueCents: number
    readonly logoUrl?: string
    readonly logoAvailableWidths?: number[]
    readonly trialDays?: number
    readonly trialMaxActivations?: number
    // pay-as-you-go: the product runs on credits, so it's a valid target
    // for credits/topup claims (the credits pickers filter on it)
    readonly needsCredits: boolean
    // null/absent = the product does not support the AI assistant;
    // read-only (AL wiring is not editable through the product endpoints)
    readonly al?: {
        readonly orchestratorModelId: number
        readonly workerModelId: number
        readonly descriptionModelId: number
    } | null
    // readonly freeSubscriptionDays?: number
}

export enum NfaOrderStatus {
    Paid = "paid",
    Refunded = "refunded"
}

export interface Order {
    id: string
    userId: string
    status: NfaOrderStatus
    storeOrderId: string
    storeOrderNumber: string
    skuId: string
    offeringId: string | null
    purchaseId: string | null
    receiptUrl: string
    createdAt: string
    // end of the self-service refund window; eligibility is derived from
    // status + this at render time (see refundState in OrderDetail)
    refundableUntil: string
    // what was paid, in cents — the amount a refund gives back; null on
    // orders predating the column, optional while a deployed backend may
    // predate the field
    totalCents?: number | null
    // upgrade orders only, single-order fetch only, while still paid: what a
    // refund would restore beyond removing this order's grants — the
    // consumed entitlements and the products whose licenses come back
    restoresOnRefund?: {
        entitlementId: string
        skuId: string
        productIds: string[]
    }[]
}

export interface Activation {
    readonly id: string
    readonly licenseId: string
    readonly deviceId: string
    readonly deviceName: string
    readonly activatedAt: string
    readonly deactivatedAt: string | null
}

export interface Device {
    readonly deviceId: string
    readonly deviceName: string
    readonly createdAt: string
    readonly updatedAt: string
}

export enum LicenseType {
    Standard = "standard",
    Demo = "demo",
    Beta = "beta",
    NFR = "nfr",
    Free = "free",
    // deposited to the owning org's members while a version is in alpha/beta
    Alpha = "alpha",
    // the dev license: deposited to a draft version's creator on creation
    // (test licensing before promoting); removed when the version leaves
    // draft
    Dev = "dev",
}

export const licenseTypeLabels: Record<LicenseType, string> = {
    [LicenseType.Standard]: "Standard",
    [LicenseType.Demo]: "Demo",
    [LicenseType.Beta]: "Beta",
    [LicenseType.NFR]: "NFR",
    [LicenseType.Free]: "Free",
    [LicenseType.Alpha]: "Alpha",
    [LicenseType.Dev]: "Dev"
}

// Types a code batch or sku grant may carry — demo is per-product trials
// only, alpha exists only via team deposits
export const GRANTABLE_LICENSE_TYPES = [LicenseType.Standard, LicenseType.NFR, LicenseType.Beta]

export enum LicenseOrigin {
    Order = "order",
    Subscription = "subscription",
    Code = "code",
    // includes alpha/beta test deposits (the license TYPE is the marker)
    Grant = "grant",
    Reward = "reward",
    FreeClaim = "free_claim",
    // product trial — refId points at the backend's trials record
    Trial = "trial",
    // originated from an iLok license the user holds
    Ilok = "ilok",
    // a reseller-distributed code (the reseller pages)
    Reseller = "reseller"
}

export const licenseOriginLabels: Record<LicenseOrigin, string> = {
  [LicenseOrigin.Order]: "Order",
  [LicenseOrigin.Subscription]: "Rent-to-Own",
  [LicenseOrigin.Code]: "Code Redemption",
  [LicenseOrigin.Grant]: "Grant",
  [LicenseOrigin.Reward]: "Points Reward",
  [LicenseOrigin.FreeClaim]: "Free Claim",
  [LicenseOrigin.Trial]: "Trial",
  [LicenseOrigin.Ilok]: "iLok",
  [LicenseOrigin.Reseller]: "Reseller Code"
}

export interface License {
    readonly id: string
    readonly userId: string
    readonly productId: string
    readonly refId?: string
    readonly createdAt: string,
    readonly url: string,
    readonly origin: LicenseOrigin,
    readonly type: LicenseType,
    readonly maxYearlyActivations?: number
    readonly maxActivations: number
    readonly expired: boolean
    readonly expiresAt?: string                     // missing for perpetual licenses
    readonly nextActivationAt?: string              // only present if there were activations within last year and there's a yearly limit
    readonly activationIds: {
        active: string[],
        revoked: string[]
    }
    readonly lockedUntil?: string                   // only present if the license is locked due to too many activations
    // set when the linked entitlement was handed over from another account —
    // refId then names the original buyer's order, not one of ours
    readonly transferredAt?: string
}

// An A.L. (AI assistant) model backing a product, resolved from the
// product's AL config for the admin product detail page
export interface AlModel {
    readonly id: number
    readonly providerId: number
    readonly providerModelId: string
    readonly name: string
}

export const providerLabels: Record<number, string> = {
    1: "OpenAI",
    2: "Anthropic",
    3: "Amazon Bedrock",
    4: "Vercel AI"
}

// One row per trial ever started (once per user+product); the trial's
// expiry is authoritative for its demo license
export interface Trial {
    readonly id: string
    readonly userId: string
    readonly productId: string
    readonly startedAt: string
    readonly expiresAt: string
    readonly expired: boolean
}

export interface Credits {
    readonly topups: number
    readonly spendings: number
}

// export interface CreditsByProduct {
//     [productId: string]: {
//         product: Product,
//         credits: Credits
//     }
// }

export enum TopupOrigin {
    Order = "order",
    Subscription = "subscription",
    CodeRedemption = "code",
    Grant = "grant",
    FreeClaim = "free_claim",
}

export interface Topup {
    readonly id: string
    readonly userId: string
    readonly productId: string
    readonly amount: number
    readonly origin: TopupOrigin
    readonly refId: string
    readonly expiresAt: string | null
    readonly createdAt: string
    readonly expired: boolean
}

export interface CreditsSummary {
    readonly userId: string
    readonly productId: string
    readonly available: number
    readonly topups: {
        readonly deposited:         // topups deposited within last month/year/all time
        {
            month: number,
            year: number,
            all: number
        },
        readonly expired: {         // topups that expired within last month/year/all time
            month: number,
            year: number,
            all: number
        },
        readonly expiring: {        // topups that will expire within next month/year/all time
            month: number,
            year: number,
            all: number
        }
    },
    readonly spendings: {           // credits spent within last month/year/all time
        month: number,
        year: number,
        all: number
    }
}

export interface CreditSpending {
    readonly id: string
    readonly amount: number
    readonly description: string
    readonly createdAt: string
}

// Reward points are not claims: they ride on Offering.rewardPoints
// (purchases/renewals) and Redemption.rewardPoints (codes).
export enum ClaimType {
    License = "license",
    Topup = "topup"
}

// One way to buy a sku; a sku with no offerings is not shipped.
export interface Offering {
    readonly id: string
    readonly skuId: string
    readonly paymentType: "one_time" | "subscription" | "free" | "reward"
    // reward offerings: the POINTS cost of claiming, not money
    readonly priceCents: number
    // reward points earned by buying through this offering: with the order
    // (one_time) or on each renewal (subscription)
    readonly rewardPoints: number
    // subscription offerings only
    readonly intervalUnit: "day" | "week" | "month" | "year" | null
    readonly intervalCount: number | null
    readonly trialDays: number | null   // the standard trial period for this plan
    // only on admin fetches (GET /admin/skus/{skuId}); null = no store
    // variant (free offerings)
    readonly storeVariantId?: string | null
    // only on admin fetches; the LemonSqueezy product the variant belongs
    // to (dashboard deep link) — null when not recorded
    readonly storeProductId?: string | null
    // only on admin fetches; true = soft-deleted (kept because historical
    // orders/subscriptions reference it) — store surfaces never see these
    readonly discontinued?: boolean
    // only on admin fetches; lifetime in days of the entitlement a purchase
    // or claim through this offering grants — null = permanent. Never set on
    // subscription offerings (expiry follows the renewal date).
    readonly licenseExpiryDays?: number | null
    // crossgrade pricing (one_time only): owners of configured outside
    // products get a discount — percents compound, amounts add, fixed = one
    // price for owning ANY of them. null/absent = the plain list price. The
    // user's resulting price arrives as Sku.crossgrade.
    readonly dynamicPricing?: "percent" | "amount" | "fixed" | null
    // only on admin fetches; fixed mode's crossgrade price, null otherwise
    readonly dynamicPriceCents?: number | null
    // only on admin fetches; percent 1..100 or cents, per dynamicPricing —
    // null in fixed mode (the entries are just the qualifying products)
    readonly discounts?: { productId: string, discount: number | null }[]
    // how a bundle upgrade through this one-time offering is priced: value →
    // by the products' values (the default), count → every product worth the
    // same. Absent on a backend older than this build (= value).
    readonly upgradePricing?: "value" | "count"
}

export interface Sku {
    readonly id: string
    readonly name: string
    readonly description: string
    readonly ownerId: string
    readonly highlights: string[]
    readonly downloadMacUrl?: string
    readonly downloadWinUrl?: string
    // the latest RELEASED version — the one serialized claims/checkouts refer
    // to; absent = nothing released yet (not sellable)
    readonly latestVersionId?: string
    readonly latestVersion?: string   // its "MAJOR.MINOR.PATCH", for display
    // how many RELEASED versions the sku has; absent = none (or a pre-versioning API)
    readonly versionCount?: number
    // the requesting user's test-build downloads — the unreleased
    // (draft/alpha/beta) version they hold an active entitlement to, the
    // status driving the link's Draft/Alpha/Beta label — Licenses-page
    // fetches only, never the store
    readonly testVersionId?: string
    readonly testVersion?: string     // its "MAJOR.MINOR.PATCH", for display
    readonly testVersionStatus?: SkuVersionStatus
    readonly testDownloadMacUrl?: string
    readonly testDownloadWinUrl?: string
    readonly imageUrl?: string
    readonly availableWidths?: number[]
    // the logo shown in place of the name (SkuName); absent = the name
    // renders as text. It is the sku's own logo unless logoProductId is
    // set: a sku without one that consists of a single product shows that
    // product's logo
    readonly logoUrl?: string
    readonly logoAvailableWidths?: number[]
    readonly logoProductId?: string
    // every way to buy this sku; empty = not shipped
    readonly offerings: Offering[]
    // per-user bundle upgrade offer; only present on authenticated store fetches
    readonly upgrade?: {
        readonly priceCents: number
        readonly valueCents: number
        // all licenses of the consumed entitlements (drives the popover's
        // "You already have" list)
        readonly revokedLicenseIds: string[]
        // the entitlements the upgrade consumes; echoed back at checkout
        readonly consumedEntitlementIds: string[]
        // the consumed entitlements' skus, deduped — display only; optional
        // while a deployed backend may predate the field
        readonly consumedSkuIds?: string[]
    }
    // the signed-in user's crossgrade price on the one-time offering (they own
    // products its dynamic pricing discounts for). Independent of upgrade —
    // personalOffer() picks the cheaper to show. 0 is claimed like a free
    // offering; anything else is echoed back at checkout for verification.
    readonly crossgrade?: {
        readonly priceCents: number
        readonly listPriceCents: number
        // the owned products that earned the discount
        readonly discountProductIds: string[]
    }
    // the sku's RUNNING sale on its one-time offering; absent = none. The
    // upgrade/crossgrade prices above already include it — this carries the
    // public promoted list price and what to say under it. Echoed (id) at
    // checkout so an ended sale is rejected instead of silently re-priced.
    readonly promotion?: {
        readonly id: string
        readonly name: string | null          // null = plain "Sale"
        readonly percentOff: number           // rounded, for display
        readonly priceCents: number           // the promoted list price
        readonly listPriceCents: number
        readonly startsAt: string
        readonly endsAt: string | null        // null = open-ended
    }
    // only on admin fetches (GET /admin/skus/{skuId}); null = redemption
    // codes cannot be minted for this sku
    readonly codePrefix?: string | null
}

// A sale scheduled on a sku (admin SKU page): what was typed — a percent off
// XOR the promoted list price — and its window. Only one may RUN on a sku
// at a time; ended ones are history (immutable, undeletable).
export type SkuPromotionStatus = "scheduled" | "running" | "ended"

export interface SkuPromotion {
    readonly id: string
    readonly skuId: string
    readonly name: string | null
    readonly discountPercent: number | null
    readonly priceCents: number | null
    readonly startsAt: string
    readonly endsAt: string | null        // null = open-ended
    readonly status: SkuPromotionStatus
    readonly createdAt: string
}

// The version lifecycle: draft (owner-only staging, fully editable,
// installer-less allowed) → alpha (the org's members hold alpha licenses,
// still editable) → beta (visible to the owner's beta testers too, still
// editable) → released (immutable, the fulfillment target). Forward-only;
// stages may be skipped, never revisited.
export enum SkuVersionStatus {
    Draft = "draft",
    Alpha = "alpha",
    Beta = "beta",
    Released = "released"
}

export const skuVersionStatusLabels: Record<SkuVersionStatus, string> = {
    [SkuVersionStatus.Draft]: "Draft",
    [SkuVersionStatus.Alpha]: "Alpha",
    [SkuVersionStatus.Beta]: "Beta",
    [SkuVersionStatus.Released]: "Released"
}

// One version of a sku — admin surface (GET /admin/skus/{skuId}/versions) and
// the "Older versions" listing. The latest RELEASED version supplies the
// sku's downloadMacUrl/downloadWinUrl. Versions are also the unit of
// licensing: claims belong to a version; draft/beta claim sets are editable,
// released ones frozen.
export interface SkuVersion {
    readonly id: string
    readonly version: string          // "MAJOR.MINOR.PATCH"
    readonly status: SkuVersionStatus
    readonly downloadMacUrl?: string
    readonly downloadWinUrl?: string
    readonly createdAt: string        // ISO
    readonly releasedAt?: string      // ISO; absent until released
    // beta only (admin listing): testers added to the owner's list are
    // auto-deposited into this version
    readonly betaAutoDeposit?: boolean
    // the version's claim set — only on the admin versions listing
    readonly claims?: Claim[]
    // distinct users with an active entitlement pinned to this version —
    // only on the admin versions listing. The alpha/beta splits (holders of
    // that test license type) feed the release dialog's dispositions.
    readonly entitledUserCount?: number
    readonly alphaUserCount?: number
    readonly betaUserCount?: number
}

// One row of the org's beta-tester list
// (GET /admin/organizations/{orgId}/beta-testers).
export interface BetaTester {
    readonly userId: string
    readonly createdAt: string
    readonly user: UserSearchResult
}

// The report of a draft/beta claim-edit license sync: revocations for removed
// claims plus the reconcile (deposit) half.
export interface SyncReport {
    readonly dryRun: boolean
    readonly versionId: string
    readonly revokedCount: number
    readonly reconcile: ReconcileReport
}

export interface Claim {
    readonly id: string
    readonly productId: string
    readonly skuVersionId: string
    readonly skuId: string            // derived from the version
    readonly type: ClaimType
    // owners of versions OLDER than this claim's version are entitled too
    readonly retroactive: boolean
    // topup claims only — license expiry lives on offerings and code batches
    readonly expiryDays?: number
    readonly amount?: number
    readonly maxYearlyActivations?: number
    readonly maxActivations?: number
}

// Summary of a POST /admin/skus/{skuId}/claims backfill — the wire report
// carries more detail (per-product/per-reason breakdowns, sample rows); only
// the summary counts are typed because only they are rendered
export interface ReconcileReport {
    readonly dryRun: boolean
    readonly skuId: string
    readonly entitlementsScanned: number
    readonly depositedCount: number
    readonly skippedCount: number
}

export enum EntitlementOrigin {
    Order = "order",
    Subscription = "subscription",
    Code = "code",
    Reward = "reward",
    // includes alpha/beta test deposits (the license TYPE is the marker)
    Grant = "grant",
    FreeClaim = "free_claim",
    // originated from an iLok license the user holds
    Ilok = "ilok",
    // a code from a list generated outside the backend and handed to a
    // reseller (the reseller pages, resellerPages.ts)
    Reseller = "reseller"
}

export const entitlementOriginLabels: Record<EntitlementOrigin, string> = {
  [EntitlementOrigin.Order]: "Order",
  [EntitlementOrigin.Subscription]: "Subscription",
  [EntitlementOrigin.Code]: "Code Redemption",
  [EntitlementOrigin.Reward]: "Points Reward",
  [EntitlementOrigin.Grant]: "Grant",
  [EntitlementOrigin.FreeClaim]: "Free Claim",
  [EntitlementOrigin.Ilok]: "iLok",
  [EntitlementOrigin.Reseller]: "Reseller Code"
}

export enum EntitlementStatus {
    Active = "active",
    Revoked = "revoked",
    Superseded = "superseded"
}

export const entitlementStatusLabels: Record<EntitlementStatus, string> = {
  [EntitlementStatus.Active]: "Active",
  [EntitlementStatus.Revoked]: "Revoked",
  [EntitlementStatus.Superseded]: "Superseded"
}

// User label for list cards and dropdowns: "id (Full Name)" — the username
// identifies the account, the human name follows in brackets when known.
// Greetings and profile pages keep using the plain full name.
export function userLabel(id: string, user?: { firstname?: string, lastname?: string }): string {
    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ")
    return name !== "" ? `${id} (${name})` : id
}

export interface UserSearchResult {
    readonly id: string
    readonly firstname?: string
    readonly lastname?: string
    readonly email?: string
    // undefined = unknown (Cognito didn't report the attribute)
    readonly emailVerified?: boolean
    readonly imageUrl?: string
    // false = account disabled in Cognito; undefined = unknown
    readonly enabled?: boolean
    // absent for users who exist in Cognito but never called the API
    readonly role?: UserRole
}

export interface Entitlement {
    readonly id: string
    readonly userId: string
    readonly skuVersionId: string
    readonly skuId: string                  // derived from the version
    readonly skuVersion: string             // derived: "MAJOR.MINOR.PATCH"
    readonly origin: EntitlementOrigin
    readonly refId: string
    readonly licenseType: LicenseType
    // whether the holder may request an iLok counterpart (a grant or code
    // batch can be issued iLok-disabled); absent on an older backend =
    // enabled
    readonly ilokEnabled?: boolean
    // who granted (grant origin only) — present on the admin surface only
    readonly grantedBy?: string | null
    readonly status: EntitlementStatus
    readonly supersededBy: string | null
    readonly revokedAt: string | null
    readonly revokedBy: string | null
    readonly revokedReason: string | null
    readonly createdAt: string
    // when the entitlement stops being valid; null = permanent — the
    // authoritative expiry its licenses derive theirs from
    readonly expiresAt: string | null
    // present while the entitlement is reserved as an upgrade checkout's
    // trade-in; may already be in the past — compare to now
    readonly lockedUntil?: string
    // set once the entitlement was handed over from another account: refId
    // names the ORIGINAL holder's order/redemption, which we can't open
    readonly transferredAt?: string
}

// Why the Licenses page can't offer a transfer right now (server-computed)
export type TransferBlock =
    | "not_active"
    | "license_type"
    | "origin"
    | "locked"
    | "expired"
    | "order_missing"
    | "order_not_paid"
    | "refund_window"
    | "ilok_pending"

// GET /me/library — an active entitlement resolved for its holder: the
// products the owned version entitles and the user's licenses deposited for
// it. Licenses the entries don't reference are trials (demo) or strays.
export interface LibraryEntry extends Entitlement {
    readonly productIds: string[]
    readonly licenseIds: string[]
    // whether the holder may hand this entitlement to another account now —
    // the server's rule, not re-derived here; absent on an older backend.
    // When blocked only by the order's refund window, transferableFrom is
    // the instant it opens.
    readonly transferable?: boolean
    readonly transferBlock?: TransferBlock
    readonly transferableFrom?: string
}

// POST /me/entitlements/{id}/transfers — the record of one hand-over
export interface EntitlementTransfer {
    readonly id: string
    readonly entitlementId: string
    readonly fromUserId: string
    readonly toUserId: string
    readonly createdAt: string
}

export interface Library {
    readonly entries: LibraryEntry[]    // newest first, test deposits included
    readonly skus: Sku[]                // one per distinct sku, with the user's test builds
    readonly products: Product[]
    readonly licenses: License[]        // ALL the user's licenses
    readonly activations: Activation[]
}

// One mint of redemption codes for a sku version (admin surface). A revoked
// batch's unredeemed codes cannot be redeemed (already-redeemed grants are
// untouched); revocation is reversible.
export interface RedemptionCodeBatch {
    readonly id: string
    readonly skuVersionId: string
    readonly skuId: string           // derived from the version
    readonly skuVersion: string      // derived: "MAJOR.MINOR.PATCH"
    readonly userId: string          // who minted the batch
    readonly comment: string | null
    readonly rewardPoints: number
    readonly licenseType: LicenseType
    // whether the entitlements the codes grant may get an iLok counterpart;
    // absent on an older backend = enabled
    readonly ilokEnabled?: boolean
    // what one code is worth in cents (the whole entitlement it grants); null
    // on batches minted before the field
    readonly codeValueCents: number | null
    // the redemption window — NOT the granted license's lifetime
    readonly expiresAt: string | null
    readonly expired: boolean
    // lifetime of the granted entitlement in days from redemption; null =
    // permanent
    readonly licenseExpiryDays: number | null
    readonly createdAt: string
    readonly revokedAt: string | null
    readonly revokedBy: string | null
    readonly revokedReason: string | null
    readonly codeCount: number
    readonly redeemedCount: number
}

// GET /admin/skus/{skuId}/code-value — what a new standard code batch is
// seeded with: the sku's average sale price, or (averageCents null) the
// offering price / product values fallback
// A guest order (admin surface): a store purchase made without an account,
// parked until an account with the same verified email loads the app, then
// claimed — an ordinary order (orderId) from there on. A claimed one whose
// order was refunded reads as refunded.
export enum GuestOrderStatus {
    Pending = "pending",
    Claimed = "claimed",
    Refunded = "refunded",
}

export interface GuestOrder {
    readonly id: string
    readonly email: string           // the address the buyer was told to sign up with
    readonly status: GuestOrderStatus
    readonly skuId: string
    readonly skuVersionId: string
    readonly skuVersion: string      // "MAJOR.MINOR.PATCH"
    readonly storeOrderNumber: string
    readonly receiptUrl: string
    readonly totalCents: number
    readonly emailedAt: string | null
    readonly userId: string | null   // who claimed it
    readonly orderId: string | null
    readonly claimedAt: string | null
    readonly createdAt: string
}

// On the /me response of the one load that materialized guest purchases
// (store purchases made signed-out and parked for this email): what just
// landed on the account, so the app can say so once.
export interface ClaimedOrder {
    readonly orderId: string
    readonly skuId: string
    readonly skuName: string
    readonly storeOrderNumber: string
}

// On the /me response of the load that linked iLok accounts registered to
// the user's email on its own: which, and how many licenses each brought.
export interface AutoLinkedIlok {
    readonly ilokId: string
    readonly issued: number
}

// GET /admin/skus/{skuId}/orders — an order row with its two tags
export interface SkuOrder extends Order {
    readonly guest: boolean          // came in as a guest purchase, claimed since
    readonly upgrade: boolean        // consumed entitlements (total = net price)
}

export interface SkuOrderStats {
    readonly paid: number
    readonly refunded: number
    readonly revenueCents: number    // paid orders with a recorded total
}

export interface GuestOrderStats {
    readonly pending: number
    readonly claimed: number         // claimed and not refunded since
    readonly refunded: number
    readonly revenueCents: number    // everything not refunded
}

export interface CodeValueSuggestion {
    readonly suggestedValueCents: number
    readonly averageCents: number | null
    readonly orderCount: number
}

// One redemption code as the API serializes it (the stored, unformatted
// value — the UI regroups it in fours).
export interface RedemptionCode {
    id: string
    code: string
    skuId: string
    batchId: string
    // reward points the code deposits on redemption, fixed at minting time
    rewardPoints: number
    createdAt: Date
    redeemedAt: Date | null
    redeemedBy: string | null
    expiresAt: Date | null
    // the batch's revocation (no per-code revoke): blocks an unredeemed code,
    // leaves a redeemed one alone
    revokedAt: Date | null
}

// The admin code lookup (GET /admin/skus/{skuId}/codes?code=): the code and
// the batch it was minted in; the status is derived client-side.
export interface RedemptionCodeLookup {
    readonly code: RedemptionCode
    readonly batch: RedemptionCodeBatch
}

// a reseller-distributed code (GET/POST /me/resellers/{reseller}/…)
export interface ResellerCode {
    readonly id: string
    readonly reseller: string
    readonly code: string           // customer-facing spelling
    readonly skuId: string
    readonly redeemedAt: string | null
}

export interface Redemption extends RedemptionCode {
    licenseIds: string[]
    topupIds: string[]
    pointTopupIds: string[]
    // the entitlement the code granted (one per code), side-loaded as
    // `entitlements` in the same response; absent on an older backend —
    // the Redemptions card then shows the code without version/type/expiry
    entitlementIds?: string[]
}

// export interface LoyaltyPointsEntry {
//     readonly id: string
//     readonly amount: number
//     readonly reason: string
//     readonly createdAt: string
// }

export interface Points {
    // matured, spendable deposits
    topups: number,
    spendings: number,
    // deposited but still held (availableAt in the future)
    pending: number
}

export enum PointsOrigin {
    Order = "order",
    Subscription = "subscription",
    Code = "code",
    Grant = "grant"
}

export interface PointTopup {
    readonly id: string
    readonly userId: string
    readonly origin: PointsOrigin
    readonly refId: string
    readonly amount: number
    readonly expiresAt: string | null
    // when the points become spendable; in the future = pending
    readonly availableAt: string
    readonly createdAt: string
}

export type PointSpendingType = "order"

export interface PointSpending {
    readonly id: string
    readonly userId: string
    readonly amount: number
    readonly refId: string
    readonly spentAt: string
    readonly type: PointSpendingType
}

export interface SubscriptionTier {
    readonly id: string
    readonly name: string
    readonly priceMonthly: number
    readonly creditsPerPeriod: number
    readonly maxActivationsPerLicense: number
    readonly products: Product[]
    readonly features: string[]
}

export type SubscriptionStatus = "on_trial" | "active" | "paused" | "past_due" | "unpaid" | "cancelled" | "expired"

export interface Subscription {
    readonly id: string
    // the version the subscription currently grants (auto-advances to the
    // sku's latest on every webhook)
    readonly skuVersionId: string
    readonly skuId: string                  // derived from the version
    readonly skuVersion: string             // derived: "MAJOR.MINOR.PATCH"
    // the billed offering; null only on subscriptions predating offerings
    readonly offeringId: string | null
    readonly userId: string
    readonly status: SubscriptionStatus
    readonly storeSubscriptionId: string
    readonly storeSubscriptionUrl: string
    readonly customerPortalUrl: string
    readonly manageSubscriptionUrl: string
    readonly updatePaymentMethodUrl: string
    readonly renewsAt: string
    readonly endsAt: string | null
}

export interface Relationship {
    readonly links: {
        readonly related: string
        readonly self: string
    }
}

export interface Checkout {
    type: "checkouts"
    id: string
    attributes: {
        store_id: number
        variant_id: number
        custom_price: number | null,      // in cents
        product_options: {
            name: string
            description: string
            media: string[]
            redirect_url: string
            receipt_button_text: string
            receipt_link_url: string
            receipt_thank_you_note: string
            enabled_variants: number[]
        },
        checkout_options: {
            embed: boolean
            media: boolean
            logo: boolean
            desc: boolean
            discount: boolean
            skip_trial: boolean
            subscription_preview: boolean
            button_color: string
        },
        checkout_data: {
            email: string
            name: string
            billing_address: string[]
            tax_number: string
            discount_code: string
            custom: string[],
            // variant_quantities: []
        },
        expires_at: string | null
        created_at: string
        updated_at: string
        test_mode: boolean
        url: string
    },
    relationships: {
        store: Relationship
        variant: Relationship
    },
    links: {
        self: string
    }
}